/**
 * Microwave cabinets (claude-docs/2026-10-30-venus-full-line-design.md,
 * group 4) — the one rule the panel, the fill, the front layout and the
 * drawing read.
 *
 *   - `microwave` — a BASE bay: Venus `BMC30`, "Appliance Base - Microwave
 *     Shelf w/ 1 Drawer", 30" wide: an open 17½" space for a microwave over
 *     one drawer (catalog p.10);
 *   - `microwave-shelf` — an UPPER bay: Venus `WM3030` / `WM3036` /
 *     `WM3042`, "Appliance Wall - Microwave Shelf w/ 2 Door", 30" wide, 30" /
 *     36" / 42" tall: doors over an open 18" microwave shelf, the shelf 15¾"
 *     deep against the box's 12" (catalog p.12).
 *
 * The user (2026-11-01): "yes, put a microwave in". The fill places one in
 * each opening (`microwaveFor`) — a `microwave` object the run owns and the
 * estimate bills at the system microwave allowance. A generic line offers the
 * same sizes. Its size is our call: 24" x 15" x 20" in a base, a countertop
 * microwave's (24" x 13" x 15") on a shelf. In a base it is BUILT IN (the
 * user, 2026-11-01: "just a built-in normal microwave. it has a panel around
 * the microwave to make it look built-in"): its face on the cabinet's,
 * centred in a trim frame filling the opening (`trimKit`, drawn by the
 * microwave's own drawing); on a shelf it stands as a countertop one.
 *
 * Pure; metres.
 */
const { CABINET } = require('./cabinetFrontSpecs');

const IN = 0.0254;
const TOL_IN = 1 / 16;

/** Each microwave front: its tier, sizes and opening. */
const MICROWAVE_CABINETS = Object.freeze({
  microwave: Object.freeze({
    tier: 'base',
    widthIn: 30,
    openingIn: 17.5,
    name: 'microwave base',
  }),
  'microwave-shelf': Object.freeze({
    tier: 'wall',
    widthIn: 30,
    heightsIn: Object.freeze([30, 36, 42]),
    openingIn: 18,
    shelfDepthIn: 15.75,
    name: 'microwave shelf cabinet',
  }),
});

/**
 * The microwave every opening holds (our call), inches: ONE microwave, the
 * base's built in and the shelf's standing on it (the user, 2026-11-01:
 * "ideally its just the same microwave? one just has the built-in
 * surround") — the size the 15¾" shelf takes.
 */
const MICROWAVE_IN = Object.freeze({ x: 24, y: 13, z: 15 });
// the room a microwave keeps from the opening's sides and top
const CLEARANCE_IN = 1;
// …and a shelf microwave's back off the wall
const SHELF_BACK_GAP_IN = 0.5;

/** Is this a microwave cabinet (base or wall)? */
const isMicrowaveCabinet = (o) =>
  o?.category?.microwaveBase === true || o?.category?.microwaveShelf === true;

const said = (m) => `${Math.round((m / IN) * 8) / 8}"`;
const listText = (xs) =>
  xs.length > 1
    ? `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`
    : xs.join('');

/**
 * Why this bay cannot wear the microwave front `frontId`, or null when it
 * can (or the front is no microwave front): the line makes it 30" wide, a
 * wall one at 30", 36" or 42" tall.
 */
function microwaveCabinetReason(run, cabinet, frontId) {
  const spec = MICROWAVE_CABINETS[frontId];
  const d = cabinet?.dimensions;
  if (!spec || !(d?.x > 0)) return null;
  if (Math.abs(d.x / IN - spec.widthIn) > TOL_IN)
    return `A ${spec.name} is ${spec.widthIn}" wide: this bay is ${said(d.x)}`;
  if (
    spec.heightsIn &&
    !spec.heightsIn.some((h) => Math.abs(h - d.y / IN) <= 0.5)
  )
    return `A ${spec.name} is ${listText(spec.heightsIn.map((h) => `${h}"`))} tall: this bay is ${said(d.y)}`;
  return null;
}

/**
 * Where a microwave cabinet's opening sits on its face, in the front
 * layout's frame (y about the cabinet's centre): `{ topY, bottomY }`, from
 * its door plane (`doorTop` / `doorBottom`, the reveals already off). A base
 * one's opening is at the top, over its drawer; a wall one's at the bottom,
 * under its doors.
 */
function microwaveOpening(frontId, { doorTop, doorBottom }) {
  const spec = MICROWAVE_CABINETS[frontId];
  const height = spec.openingIn * IN - CABINET.doorReveal;
  return spec.tier === 'base'
    ? { topY: doorTop, bottomY: doorTop - height }
    : { topY: doorBottom + height, bottomY: doorBottom };
}

/** A wall microwave shelf's depth, metres. */
const microwaveShelfDepthM = () =>
  MICROWAVE_CABINETS['microwave-shelf'].shelfDepthIn * IN;

/**
 * The microwave in this cabinet's opening, as the run places it, centred
 * across the opening: a base one built in — its face on the cabinet's,
 * centred in the opening, with the trim frame round it (`trimKit`: the
 * frame's outer `width` and `height`, and `y`, its middle above the
 * microwave's, metres — the opening's, whatever microwave stands in it); a
 * wall one standing on the shelf against the wall, out over its lip. Null for
 * any other cabinet.
 */
function microwaveFor(cabinet) {
  if (!isMicrowaveCabinet(cabinet)) return null;
  const base = cabinet.category.microwaveBase === true;
  const frontId = base ? 'microwave' : 'microwave-shelf';
  const size = MICROWAVE_IN;
  const d = cabinet.dimensions;
  const h = d.y;
  // the door plane, in the cabinet's own frame (y about its centre)
  const body = base ? h - CABINET.toeKickHeight : h;
  const bodyCenter = base ? CABINET.toeKickHeight / 2 : 0;
  const doorHalf = body / 2 - CABINET.doorReveal;
  const opening = microwaveOpening(frontId, {
    doorTop: bodyCenter + doorHalf,
    doorBottom: bodyCenter - doorHalf,
  });
  // never more than the opening holds: a bay can be re-fitted narrower than
  // the line's 30" after its front was picked (the user, 2026-11-01)
  const fit = (want, room) => Math.max(0, Math.min(want * IN, room));
  const sizeM = {
    x: fit(size.x, d.x - 2 * CABINET.panelThick - 2 * CLEARANCE_IN * IN),
    y: fit(
      size.y,
      opening.topY - opening.bottomY - CABINET.panelThick - CLEARANCE_IN * IN
    ),
    z: size.z * IN,
  };
  const openingMid = (opening.topY + opening.bottomY) / 2;
  const ly = base
    ? openingMid
    : opening.bottomY + CABINET.panelThick + sizeM.y / 2;
  const lz = base
    ? d.z / 2 - sizeM.z / 2
    : -d.z / 2 + SHELF_BACK_GAP_IN * IN + sizeM.z / 2;
  // a base one's trim frame fills the opening between the cabinet's sides
  const trimKit = base
    ? {
        width: d.x - 2 * CABINET.panelThick,
        height: opening.topY - opening.bottomY,
        y: openingMid - ly,
      }
    : null;
  const ry = cabinet.rotation?.y || 0;
  return {
    id: `microwave_${cabinet.id}`,
    type: 'microwave',
    name: 'Microwave',
    category: { microwave: true },
    addedBy: 'manual-cabinet-run',
    microwaveCabinetId: cabinet.id,
    ...(cabinet.bayId ? { bayId: cabinet.bayId } : {}),
    ...(cabinet.runId ? { runId: cabinet.runId } : {}),
    parents: [{ id: cabinet.id, type: cabinet.type }],
    center: {
      x: cabinet.center.x + lz * Math.sin(ry),
      y: cabinet.center.y + ly,
      z: cabinet.center.z + lz * Math.cos(ry),
    },
    dimensions: sizeM,
    ...(trimKit ? { trimKit } : {}),
    // the cabinet's own facing
    rotation: { x: 0, y: 0, z: 0, ...cabinet.rotation },
  };
}

module.exports = {
  MICROWAVE_CABINETS,
  MICROWAVE_IN,
  isMicrowaveCabinet,
  microwaveCabinetReason,
  microwaveOpening,
  microwaveShelfDepthM,
  microwaveFor,
};
